import React from 'react';
import { BarChart, Trash2, Layers, HistoryIcon } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных расчетов НДС
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation,
  calculationHistory 
}) => {
  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Если нет сохраненных расчетов и истории
  if ((!savedCalculations || savedCalculations.length === 0) && 
      (!calculationHistory || calculationHistory.length === 0)) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          История расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты и история вычислений.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      {/* Сохраненные расчеты */}
      {savedCalculations && savedCalculations.length > 0 && (
        <div className="mb-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
              Сохраненные расчеты
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
            </span>
          </div>

          <div className="space-y-3">
            {savedCalculations.map((calc) => (
              <div 
                key={calc.id} 
                className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {calc.name}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="xs"
                      variant="ghost"
                      icon={<Layers className="w-4 h-4" />}
                      onClick={() => loadSavedCalculation(calc.id)}
                      title="Загрузить параметры"
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      icon={<Trash2 className="w-4 h-4 text-red-500" />}
                      onClick={() => deleteSavedCalculation(calc.id)}
                      title="Удалить"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between mb-1">
                    <span>Тип:</span>
                    <span>
                      {calc.calculationType === 'add' ? 'Начисление НДС' : 'Выделение НДС'}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Ставка:</span>
                    <span>
                      {calc.formData.taxRate === 'exempt' ? 'Без НДС' : `${calc.formData.taxRate}%`}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Дата:</span>
                    <span>
                      {formatDate(calc.date)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Сумма:</span>
                    <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                      {calc.results.isMultipleItems 
                        ? `${formatCurrency(calc.results.totalAmountWithVAT)} (с НДС)`
                        : formatCurrency(calc.results.amountWithVAT)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* История расчетов */}
      {calculationHistory && calculationHistory.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
              <HistoryIcon className="w-4 h-4 mr-2 text-gray-500" />
              История расчетов
            </h3>
          </div>

          <div className="overflow-hidden overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Дата</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Сумма</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {calculationHistory.slice(0, 5).map((calc) => (
                  <tr 
                    key={calc.id} 
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(calc.date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs text-gray-900 dark:text-white">
                      {formatCurrency(calc.amount)}
                      <span className="ml-1 text-gray-500 dark:text-gray-400">
                        {calc.taxRate === 'exempt' ? 'без НДС' : `(${calc.taxRate}%)`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedCalculationsSidebar;